/**
 * TPU-style hover magnifier for Origin Medical three-column PDPs.
 * Lens + zoom panel append to <body> so sticky/overflow ancestors cannot clip them.
 * Pointer-fine + desktop breakpoint only; click-to-open zoom dialog stays intact.
 */
(() => {
  const init = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const layout = document.querySelector(
      '.product-information__grid--three-column[data-om-hover-zoom], .product-information__grid--three-column'
    );
    if (!layout) return;

    const gallery = layout.querySelector('.product-information__media');
    if (!gallery) return;

    const bpRaw = (layout.dataset.omZoomBp || '1024px').trim();
    const bp = /^\d+(\.\d+)?px$/.test(bpRaw) ? bpRaw : '1024px';
    const desktopMq = window.matchMedia(`(min-width: ${bp})`);

    const ZOOM = 2.5;
    const MIN_PANEL_W = 320;
    const MIN_PANEL_H = 320;
    const FALLBACK_GAP = 24;
    const VIEWPORT_BOTTOM_MARGIN = 24;
    const ITEM_SELECTOR =
      'slideshow-slide.product-media-container--image, slideshow-slide.product-media-container--zoomable, .product-media-container--image, .product-media-container--zoomable:not(.product-media-container--video):not(.product-media-container--model)';

    const lens = document.createElement('div');
    lens.className = 'om-zoom-lens';
    lens.hidden = true;
    lens.setAttribute('aria-hidden', 'true');

    const panel = document.createElement('div');
    panel.className = 'om-zoom-panel';
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');

    document.body.append(lens, panel);

    let activeItem = null;
    let activeImg = null;
    let panelW = 0;
    let panelH = 0;
    let lensW = 0;
    let lensH = 0;

    const headerOffset = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 96;
    };

    // Shopify image_tag often emits data_max_resolution (underscore) instead of data-max-resolution.
    const zoomSrc = (item, img) =>
      (item && (item.getAttribute('data-om-zoom-src') || item.dataset.omZoomSrc)) ||
      (img &&
        (img.getAttribute('data-max-resolution') ||
          img.getAttribute('data_max_resolution') ||
          img.currentSrc ||
          img.src)) ||
      '';

    const sizePanelAndLens = () => {
      const galleryRect = gallery.getBoundingClientRect();
      const layoutRect = layout.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(layout).columnGap) || FALLBACK_GAP;
      const vh = document.documentElement.clientHeight;

      const left = Math.round(galleryRect.right + gap);
      const right = Math.round(layoutRect.right);
      panelW = Math.max(MIN_PANEL_W, right - left);

      const top = headerOffset();
      panelH = Math.max(MIN_PANEL_H, vh - top - VIEWPORT_BOTTOM_MARGIN);

      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.width = `${panelW}px`;
      panel.style.height = `${panelH}px`;

      lensW = panelW / ZOOM;
      lensH = panelH / ZOOM;
      if (activeImg) {
        const r = activeImg.getBoundingClientRect();
        const scale = Math.min(1, r.width / Math.max(1, lensW), r.height / Math.max(1, lensH));
        lensW = Math.floor(lensW * scale);
        lensH = Math.floor(lensH * scale);
      }
      lens.style.width = `${lensW}px`;
      lens.style.height = `${lensH}px`;
    };

    const onLeave = () => {
      activeItem = null;
      activeImg = null;
      lens.hidden = true;
      panel.hidden = true;
    };

    const resolveItem = (target) => {
      const item = target && target.closest && target.closest(ITEM_SELECTOR);
      if (!item || !gallery.contains(item)) return null;
      if (item.matches('slideshow-slide') && item.getAttribute('aria-hidden') === 'true') return null;
      if (item.classList.contains('product-media-container--video')) return null;
      if (item.classList.contains('product-media-container--model')) return null;
      return item;
    };

    const onEnter = (item) => {
      if (!desktopMq.matches) return;
      const img = item.querySelector('img.product-media__image, img');
      const src = zoomSrc(item, img);
      if (!img || !src) return;

      activeItem = item;
      activeImg = img;
      panel.style.backgroundImage = `url("${src}")`;
      sizePanelAndLens();
      lens.hidden = false;
      panel.hidden = false;
    };

    const onMove = (e) => {
      if (!activeImg || !activeItem || !desktopMq.matches) return;

      // Hit-test the slide/container (not just the contain-fit img) so letterboxing
      // does not instantly dismiss the TPU-style preview.
      const itemRect = activeItem.getBoundingClientRect();
      if (
        e.clientX < itemRect.left ||
        e.clientY < itemRect.top ||
        e.clientX > itemRect.right ||
        e.clientY > itemRect.bottom
      ) {
        onLeave();
        return;
      }

      const rect = activeImg.getBoundingClientRect();
      let lx = e.clientX - lensW / 2;
      let ly = e.clientY - lensH / 2;
      lx = Math.max(rect.left, Math.min(rect.right - lensW, lx));
      ly = Math.max(rect.top, Math.min(rect.bottom - lensH, ly));
      lens.style.left = `${lx}px`;
      lens.style.top = `${ly}px`;

      const ratioX = (lx - rect.left) / Math.max(1, rect.width - lensW);
      const ratioY = (ly - rect.top) / Math.max(1, rect.height - lensH);

      const bgW = (rect.width * panelW) / Math.max(1, lensW);
      const bgH = (rect.height * panelH) / Math.max(1, lensH);
      const bgX = -ratioX * (bgW - panelW);
      const bgY = -ratioY * (bgH - panelH);

      panel.style.backgroundSize = `${bgW}px ${bgH}px`;
      panel.style.backgroundPosition = `${bgX}px ${bgY}px`;
    };

    // Delegation: slideshow slides get re-shown / cloned; binding once is fragile.
    gallery.addEventListener('pointerenter', (e) => {
      const item = resolveItem(e.target);
      if (!item) return;
      if (item !== activeItem) onEnter(item);
    }, true);

    gallery.addEventListener('pointermove', (e) => {
      if (!desktopMq.matches) return;
      const item = resolveItem(e.target);
      if (!item) {
        if (activeItem) onLeave();
        return;
      }
      if (item !== activeItem) onEnter(item);
      onMove(e);
    });

    gallery.addEventListener('pointerleave', onLeave);

    window.addEventListener('scroll', onLeave, { passive: true });
    window.addEventListener('resize', onLeave);
    desktopMq.addEventListener('change', onLeave);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
